import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Loader2, Edit3, X, Check, Users as UsersIcon, Shield, Eye, EyeOff, KeyRound, Mail } from 'lucide-react';import { api, errorMessage } from '../../lib/api';
import { useBrand } from '../../context/BrandContext';
import { AdminResetPasswordDialog } from '../../components/PasswordDialogs';

const ROLES = [
  { id: 'admin', label: 'Admin', desc: 'Full access, users + discount' },
  { id: 'itanta_user', label: 'Internal user', desc: 'Price lists + quotes' },
  { id: 'partner_user', label: 'Partner User', desc: 'Price lists + quotes' },
];

const RoleBadge = ({ role }) => {
  const cls = role === 'admin'
    ? 'bg-emerald-400/15 text-emerald-300 border-emerald-400/30'
    : role === 'itanta_user'
      ? 'bg-cyan-400/15 text-cyan-300 border-cyan-400/30'
      : 'bg-purple-400/15 text-purple-300 border-purple-400/30';
  const label = ROLES.find((r) => r.id === role)?.label || role;
  return (
    <span className={`inline-flex items-center gap-1 text-[10.5px] tracking-[0.14em] font-semibold uppercase px-2 py-0.5 rounded-full border ${cls}`}>
      <Shield className="w-3 h-3" />
      {label}
    </span>
  );
};

const UsersPage = () => {
  const { brand } = useBrand();
  const [users, setUsers] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const [form, setForm] = useState({
    email: '', full_name: '', role: 'itanta_user', org: 'itanta', partner_name: '', password: '',
    allowed_products: ['pti', 'core', 'streamline'],
    allowed_pti_modes: ['concurrent', 'assets'],
    region_id: '',
    welcome_message: '',
    cc_emails_text: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([api.get('/users'), api.get('/regions')]);
      setUsers(u.data);
      setRegions(r.data);
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const submit = async (e, { withEmail }) => {
    e.preventDefault();
    setErr(''); setSaving(true);
    try {
      const body = { ...form };
      if (body.org !== 'partner') body.partner_name = null;
      if (!body.region_id) delete body.region_id;
      // Parse comma / space / semicolon-separated CC emails.
      body.cc_emails = (form.cc_emails_text || '')
        .split(/[,;\s]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      body.send_welcome_email = !!withEmail;
      delete body.cc_emails_text;
      await api.post('/users', body);
      setShowForm(false);
      setForm({
        email: '', full_name: '', role: 'itanta_user', org: 'itanta', partner_name: '', password: '',
        allowed_products: ['pti', 'core', 'streamline'],
        allowed_pti_modes: ['concurrent', 'assets'],
        region_id: '',
        welcome_message: '',
        cc_emails_text: '',
      });
      await load();
      if (withEmail) setResendMsg(`User created and invite emailed to ${body.email}.`);
      else setResendMsg(`User created. Click the mail icon on their row to email them a sign-in invite (we'll generate a temp password automatically).`);
      setTimeout(() => setResendMsg(''), 6000);
    } catch (ex) {
      setErr(errorMessage(ex, 'Failed to create user'));
    }
    setSaving(false);
  };

  const [editingUser, setEditingUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [resendingId, setResendingId] = useState('');
  const [resendMsg, setResendMsg] = useState('');  // transient toast at bottom

  const resendInvite = async (u) => {
    if (!window.confirm(`Email a sign-in invite to ${u.email}?\n\nA fresh temporary password will be generated and emailed to them (their old password will stop working). The email uses the brand currently active in Settings.`)) return;
    setResendingId(u.id);
    setResendMsg('');
    try {
      const r = await api.post(`/users/${u.id}/resend-invite`, {});
      const email = r.data?.email || {};
      if (email.sent) {
        setResendMsg(`Invite emailed to ${u.email} with a fresh temporary password (brand: ${email.brand || 'current'}).`);
      } else if (email.mocked) {
        setResendMsg(`Email is in mocked mode (EMERGENT_EMAIL_KEY missing) — no email was actually sent.`);
      } else {
        setResendMsg(`Could not send invite: ${email.error || 'unknown error'}`);
      }
    } catch (ex) {
      setResendMsg(errorMessage(ex, 'Could not resend invite'));
    } finally {
      setResendingId('');
      setTimeout(() => setResendMsg(''), 7000);
    }
  };
  const openEdit = (u) => setEditingUser({
    ...u,
    allowed_products: u.allowed_products || ['pti', 'core', 'streamline'],
    allowed_pti_modes: u.allowed_pti_modes || ['concurrent', 'assets'],
    region_id: u.region_id || '',
  });
  const saveEdit = async () => {
    if (!editingUser) return;
    try {
      await api.patch(`/users/${editingUser.id}`, {
        full_name: editingUser.full_name,
        role: editingUser.role,
        org: editingUser.org,
        partner_name: editingUser.partner_name || null,
        active: editingUser.active,
        allowed_products: editingUser.allowed_products,
        allowed_pti_modes: editingUser.allowed_pti_modes,
        region_id: editingUser.region_id || null,
      });
      setEditingUser(null);
      await load();
    } catch (e) { alert(errorMessage(e, 'Failed')); }
  };

  const toggleInArray = (arr, val) => arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val];

  const toggleActive = async (u) => {
    await api.patch(`/users/${u.id}`, { active: !u.active });
    load();
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    await api.delete(`/users/${u.id}`);
    load();
  };

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-6xl">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="flex items-center gap-2 text-[11px] tracking-[0.22em] font-semibold uppercase text-emerald-300">
            <UsersIcon className="w-3.5 h-3.5" /> Users
          </div>
          <h1 className="mt-2 text-3xl font-semibold text-white tracking-tight">User management</h1>
          <p className="mt-1 text-white/55">Invite internal employees or Partner users. Admin manages discount &amp; list prices; others manage only price lists.</p>
        </div>
        <button
          onClick={() => setShowForm((s) => !s)}
          className="h-10 px-4 rounded-lg bg-emerald-400 hover:bg-emerald-300 text-black font-semibold text-sm flex items-center gap-2 transition-colors"
        >
          <Plus className="w-4 h-4" /> Invite user
        </button>
      </div>

      {showForm && (
        <form onSubmit={(e) => submit(e, { withEmail: true })} className="mb-8 rounded-2xl border border-white/10 bg-white/[0.02] p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="text-white font-semibold">Invite new user</div>
            <button type="button" onClick={() => setShowForm(false)} className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Full name" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} required />
            <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
            <Field label="Password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} required minLength={6} />
            <div>
              <div className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60 mb-2">Role</div>
              <div className="grid grid-cols-3 gap-2">
                {ROLES.map((r) => (
                  <button key={r.id} type="button" onClick={() => setForm({ ...form, role: r.id, org: r.id === 'partner_user' ? 'partner' : 'itanta' })}
                    className={`text-left rounded-lg border p-2.5 text-[12px] transition-colors ${
                      form.role === r.id ? 'border-emerald-400/60 bg-emerald-400/[0.06]' : 'border-white/10 bg-white/[0.02] hover:border-white/20'
                    }`}>
                    <div className="font-semibold text-white text-[13px]">{r.label}</div>
                    <div className="text-white/50 mt-0.5 text-[11px]">{r.desc}</div>
                  </button>
                ))}
              </div>
            </div>
            {form.org === 'partner' && (
              <Field label="Partner name" value={form.partner_name} onChange={(v) => setForm({ ...form, partner_name: v })} />
            )}
            <div className={form.org === 'partner' ? '' : 'md:col-span-2'}>
              <div className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60 mb-2">Region · Currency</div>
              <select
                data-testid="user-region-select"
                value={form.region_id}
                onChange={(e) => setForm({ ...form, region_id: e.target.value })}
                className="w-full h-10 px-3 rounded-lg bg-white/[0.03] border border-white/10 text-white text-[14px] focus:outline-none focus:border-cyan-400/50"
              >
                <option value="" className="bg-[#12121a]">— No region (defaults to USD) —</option>
                {regions.map((r) => (
                  <option key={r.id} value={r.id} className="bg-[#12121a]">
                    {r.name} · {r.default_currency}
                  </option>
                ))}
              </select>
              <div className="mt-1.5 text-[11px] text-white/40">
                The user's pricing calculator will display amounts in this region's currency.
              </div>
            </div>
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-white/5">
              <div>
                <div className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60 mb-2">Allowed products</div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: 'streamline', label: 'Streamline' },
                    { id: 'core', label: 'Core' },
                    { id: 'pti', label: 'PTI' },
                  ].map((p) => (
                    <button key={p.id} type="button"
                      onClick={() => setForm({ ...form, allowed_products: toggleInArray(form.allowed_products, p.id) })}
                      className={`h-10 rounded-lg border text-[13px] font-semibold uppercase transition-colors ${
                        form.allowed_products.includes(p.id)
                          ? 'border-emerald-400/60 bg-emerald-400/[0.08] text-white'
                          : 'border-white/10 bg-white/[0.02] text-white/50 hover:border-white/20'
                      }`}>{p.label}</button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60 mb-2">PTI pricing modes</div>
                <div className="grid grid-cols-2 gap-2">
                  {[{ id: 'concurrent', label: 'Hybrid / Tags' }, { id: 'assets', label: 'Assets & Users' }].map((m) => (
                    <button key={m.id} type="button"
                      disabled={!form.allowed_products.includes('pti') && !form.allowed_products.includes('streamline')}
                      onClick={() => setForm({ ...form, allowed_pti_modes: toggleInArray(form.allowed_pti_modes, m.id) })}
                      className={`h-10 rounded-lg border text-[12px] font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                        form.allowed_pti_modes.includes(m.id)
                          ? 'border-cyan-400/60 bg-cyan-400/[0.08] text-white'
                          : 'border-white/10 bg-white/[0.02] text-white/50 hover:border-white/20'
                      }`}>{m.label}</button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          {err && <div className="mt-3 text-[13px] text-red-300">{err}</div>}

          <div className="mt-6 pt-5 border-t border-white/5">
            <div className="text-[13.5px] font-semibold text-white">Email invite details <span className="text-white/40 font-normal">(used only if you click "Create & email invite")</span></div>
            <div className="mt-3 space-y-3">
              <div>
                <div className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/50 mb-1.5">
                  CC <span className="text-white/30 normal-case tracking-normal">(optional)</span>
                </div>
                <input
                  type="text"
                  value={form.cc_emails_text}
                  onChange={(e) => setForm({ ...form, cc_emails_text: e.target.value })}
                  placeholder="manager@company.com, team@company.com"
                  data-testid="welcome-cc-input"
                  className="w-full h-10 px-3 rounded-lg bg-white/[0.03] border border-white/10 text-white placeholder:text-white/30 text-[13.5px] focus:outline-none focus:border-cyan-400/50"
                />
                <div className="mt-1 text-[11px] text-white/40">
                  Comma, space or semicolon separated. These addresses receive the same welcome email in CC.
                </div>
              </div>
              <div>
                <div className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/50 mb-1.5">
                  Personal note <span className="text-white/30 normal-case tracking-normal">(optional)</span>
                </div>
                <textarea
                  rows={3}
                  value={form.welcome_message}
                  onChange={(e) => setForm({ ...form, welcome_message: e.target.value })}
                  placeholder="e.g. Welcome aboard! Reach out if you need anything — looking forward to working with you."
                  data-testid="welcome-message-input"
                  className="w-full px-3 py-2.5 rounded-lg bg-white/[0.03] border border-white/10 text-white placeholder:text-white/30 text-[13.5px] focus:outline-none focus:border-cyan-400/50 resize-none"
                />
                <div className="mt-1 text-[11px] text-white/40">
                  Appears at the top of the email, above the credentials.
                </div>
              </div>
            </div>
          </div>

          <div className="mt-5 flex items-center flex-wrap gap-3">
            <button
              type="button"
              onClick={(e) => submit(e, { withEmail: false })}
              disabled={saving}
              data-testid="create-user-only-btn"
              className="h-10 px-5 rounded-lg bg-white/[0.05] hover:bg-white/[0.08] border border-white/10 text-white font-semibold text-sm flex items-center gap-2 disabled:opacity-60"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Create user
            </button>
            <button
              type="submit"
              onClick={(e) => submit(e, { withEmail: true })}
              disabled={saving}
              data-testid="create-and-email-user-btn"
              className="h-10 px-5 rounded-lg bg-emerald-400 hover:bg-emerald-300 disabled:opacity-60 text-black font-semibold text-sm flex items-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              <Mail className="w-4 h-4" />
              Create &amp; email invite
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="h-10 px-5 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/80 font-semibold text-sm">Cancel</button>
          </div>
        </form>
      )}

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <div className="overflow-x-auto"><table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="border-b border-white/10 text-left text-[10px] tracking-[0.2em] font-semibold uppercase text-white/50">
              <th className="px-6 py-4">User</th>
              <th className="px-4 py-4">Role</th>
              <th className="px-4 py-4">Org</th>
              <th className="px-4 py-4">Status</th>
              <th className="px-4 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-6 py-8 text-center text-white/40">Loading…</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={5} className="px-6 py-8 text-center text-white/40">No users yet</td></tr>
            ) : users.map((u) => (
              <tr key={u.id} className="border-b border-white/5 last:border-0">
                <td className="px-6 py-4">
                  <div className="font-medium text-white">{u.full_name}</div>
                  <div className="text-[12px] text-white/50">{u.email}</div>
                </td>
                <td className="px-4 py-4"><RoleBadge role={u.role} /></td>
                <td className="px-4 py-4 text-white/75">
                  {u.org === 'partner' ? (
                    <span>Partner <span className="text-white/40">· {u.partner_name || '—'}</span></span>
                  ) : brand.name}
                </td>
                <td className="px-4 py-4">
                  <button onClick={() => toggleActive(u)} className={`inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1 rounded-full border ${
                    u.active ? 'text-emerald-300 border-emerald-400/30 bg-emerald-400/10' : 'text-white/40 border-white/15 bg-white/[0.03]'
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${u.active ? 'bg-emerald-400' : 'bg-white/30'}`} />
                    {u.active ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td className="px-4 py-4 text-right space-x-1">
                  <button
                    onClick={() => resendInvite(u)}
                    disabled={resendingId === u.id}
                    data-testid={`resend-invite-btn-${u.id}`}
                    title="Resend sign-in link (uses the current brand)"
                    className="h-8 w-8 rounded-lg text-white/50 hover:text-cyan-300 hover:bg-cyan-400/10 inline-flex items-center justify-center disabled:opacity-50"
                  >
                    {resendingId === u.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => setResetUser(u)}
                    data-testid={`reset-password-btn-${u.id}`}
                    title="Reset password"
                    className="h-8 w-8 rounded-lg text-white/50 hover:text-amber-300 hover:bg-amber-400/10 inline-flex items-center justify-center"
                  >
                    <KeyRound className="w-4 h-4" />
                  </button>
                  <button onClick={() => openEdit(u)} className="text-[13px] text-cyan-300 hover:text-cyan-200 font-semibold px-2">Edit</button>
                  <button onClick={() => remove(u)} className="h-8 w-8 rounded-lg text-white/40 hover:text-red-300 hover:bg-red-400/10 inline-flex items-center justify-center">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>

      {editingUser && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center px-4 py-6 overflow-y-auto">
          <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#12121a] p-6">
            <div className="flex items-center justify-between mb-5">
              <div className="text-white font-semibold text-lg">Edit · {editingUser.email}</div>
              <button onClick={() => setEditingUser(null)} className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-4">
              <Field label="Full name" value={editingUser.full_name} onChange={(v) => setEditingUser({ ...editingUser, full_name: v })} />
              <div>
                <div className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60 mb-2">Region · Currency</div>
                <select
                  data-testid="user-edit-region-select"
                  value={editingUser.region_id || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, region_id: e.target.value })}
                  className="w-full h-10 px-3 rounded-lg bg-white/[0.03] border border-white/10 text-white text-[14px] focus:outline-none focus:border-cyan-400/50"
                >
                  <option value="" className="bg-[#12121a]">— No region (defaults to USD) —</option>
                  {regions.map((r) => (
                    <option key={r.id} value={r.id} className="bg-[#12121a]">
                      {r.name} · {r.default_currency}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <div className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60 mb-2">Role</div>
                <div className="grid grid-cols-3 gap-2">
                  {ROLES.map((r) => (
                    <button key={r.id} type="button" onClick={() => setEditingUser({ ...editingUser, role: r.id })}
                      className={`h-10 rounded-lg border text-[12px] font-semibold transition-colors ${
                        editingUser.role === r.id ? 'border-emerald-400/60 bg-emerald-400/[0.06] text-white' : 'border-white/10 bg-white/[0.02] text-white/70 hover:border-white/20'
                      }`}>{r.label}</button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-white/5">
                <div>
                  <div className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60 mb-2">Allowed products</div>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { id: 'streamline', label: 'Streamline' },
                      { id: 'core', label: 'Core' },
                      { id: 'pti', label: 'PTI' },
                    ].map((p) => (
                      <button key={p.id} type="button"
                        onClick={() => setEditingUser({ ...editingUser, allowed_products: toggleInArray(editingUser.allowed_products, p.id) })}
                        className={`h-10 rounded-lg border text-[13px] font-semibold uppercase transition-colors ${
                          editingUser.allowed_products.includes(p.id)
                            ? 'border-emerald-400/60 bg-emerald-400/[0.08] text-white'
                            : 'border-white/10 bg-white/[0.02] text-white/50 hover:border-white/20'
                        }`}>{p.label}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60 mb-2">PTI modes</div>
                  <div className="grid grid-cols-2 gap-2">
                    {[{ id: 'concurrent', label: 'Hybrid / Tags' }, { id: 'assets', label: 'Assets & Users' }].map((m) => (
                      <button key={m.id} type="button"
                        disabled={!editingUser.allowed_products.includes('pti') && !editingUser.allowed_products.includes('streamline')}
                        onClick={() => setEditingUser({ ...editingUser, allowed_pti_modes: toggleInArray(editingUser.allowed_pti_modes, m.id) })}
                        className={`h-10 rounded-lg border text-[11.5px] font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                          editingUser.allowed_pti_modes.includes(m.id)
                            ? 'border-cyan-400/60 bg-cyan-400/[0.08] text-white'
                            : 'border-white/10 bg-white/[0.02] text-white/50 hover:border-white/20'
                        }`}>{m.label}</button>
                    ))}
                  </div>
                </div>
              </div>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={editingUser.active} onChange={(e) => setEditingUser({ ...editingUser, active: e.target.checked })} className="h-4 w-4" />
                <span className="text-[13px] text-white/70">Active</span>
              </label>
            </div>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button onClick={() => setEditingUser(null)} className="h-10 px-5 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/80 font-semibold text-sm">Cancel</button>
              <button onClick={saveEdit} className="h-10 px-5 rounded-lg bg-emerald-400 hover:bg-emerald-300 text-black font-semibold text-sm">Save</button>
            </div>
          </div>
        </div>
      )}
      <AdminResetPasswordDialog open={!!resetUser} user={resetUser} onClose={() => setResetUser(null)} />
      {resendMsg && (
        <div
          data-testid="resend-invite-toast"
          className="fixed bottom-6 right-6 z-50 max-w-sm rounded-xl border border-white/10 bg-[#12121a] text-white/90 px-4 py-3 text-[13px] shadow-2xl flex items-start gap-2"
        >
          <Mail className="w-4 h-4 text-cyan-300 flex-shrink-0 mt-0.5" />
          <span>{resendMsg}</span>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text', required, minLength }) => {
  const [reveal, setReveal] = React.useState(false);
  const isPassword = type === 'password';
  const inputType = isPassword && reveal ? 'text' : type;
  return (
  <label className="block">
    <span className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60">{label}</span>
    <div className="mt-2 relative">
      <input
        type={inputType}
        required={required}
        minLength={minLength}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full h-10 pl-3 rounded-lg bg-white/[0.03] border border-white/10 text-white focus:outline-none focus:border-cyan-400/50 focus:ring-2 focus:ring-cyan-400/20 text-[14px] ${isPassword ? 'pr-10' : 'pr-3'}`}
      />
      {isPassword && (
        <button
          type="button"
          onClick={() => setReveal((r) => !r)}
          tabIndex={-1}
          aria-label={reveal ? 'Hide password' : 'Show password'}
          className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-md text-white/50 hover:text-white hover:bg-white/5 flex items-center justify-center transition-colors"
        >
          {reveal ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      )}
    </div>
  </label>
  );
};

export default UsersPage;
